import { useTranslation } from 'react-i18next'
import { Logo } from '@/components/layout/Logo'
import { LanguageSelector } from '@/components/layout/LanguageSelector'

interface AuthLayoutProps {
  children: React.ReactNode
  /**
   * Optional title shown above the card
   */
  title?: string
  /**
   * Optional subtitle/description
   */
  subtitle?: string
}

/**
 * AuthLayout - Centered card layout for authentication pages
 * Uses cream background with a white card, matching Bonnie branding
 */
export function AuthLayout({ children, title, subtitle }: AuthLayoutProps) {
  const { t } = useTranslation()

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-cream px-4 py-8 dark:bg-gray-950">
      {/* Logo */}
      <div className="mb-8">
        <Logo width={140} />
      </div>

      {/* Card container */}
      <div className="w-full max-w-md">
        <div className="rounded-xl bg-white p-8 shadow-lg dark:bg-gray-900">
          {/* Header */}
          {(title || subtitle) && (
            <div className="mb-6 text-center">
              {title && (
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {title}
                </h1>
              )}
              {subtitle && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {subtitle}
                </p>
              )}
            </div>
          )}

          {/* Content */}
          {children}
        </div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            © {new Date().getFullYear()} Bonnie. {t('app.auth.all_rights_reserved')}
          </p>
          <LanguageSelector guest variant="compact" showLabel={false} />
        </div>
      </div>
    </div>
  )
}

export default AuthLayout
